import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../../components/ui/card';
import { toast } from 'sonner';
import { Trophy, Mail, Lock, User, Loader2, Eye, EyeOff, CheckCircle, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

// Google Icon Component
const GoogleIcon = () => (
    <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
    </svg>
);

export default function RegisterPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { register, loginWithGoogle } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [success, setSuccess] = useState(false);
    
    const initialEmail = searchParams.get('email') || '';
    const [formData, setFormData] = useState({
        name: '',
        email: initialEmail,
        password: '',
        confirmPassword: ''
    });

    const handleGoogleLogin = async () => {
        setGoogleLoading(true);
        try {
            await loginWithGoogle();
        } catch (error) {
            toast.error('Error al conectar con Google');
            setGoogleLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.password !== formData.confirmPassword) {
            toast.error('Las contraseñas no coinciden');
            return;
        }

        if (formData.password.length < 6) {
            toast.error('La contraseña debe tener al menos 6 caracteres');
            return;
        }

        setLoading(true);

        try {
            await register(formData.email, formData.password, formData.name);
            setSuccess(true);
            toast.success('¡Cuenta creada! Revisa tu correo para verificar tu cuenta.');
        } catch (error) {
            const message = error.response?.data?.detail || 'Error al crear la cuenta';
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5 relative">
                <button
                    onClick={toggleTheme}
                    className="absolute top-5 right-5 w-10 h-10 rounded-xl bg-card border border-border flex items-center justify-center transition-all hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 active:scale-90"
                    data-testid="register-theme-toggle"
                >
                    {theme === 'dark' ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-600" />}
                </button>
                <div className="w-full max-w-md">
                    <Card className="border-border/50 shadow-xl">
                        <CardContent className="pt-8 pb-8 text-center">
                            <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <CheckCircle className="w-8 h-8 text-emerald-500" />
                            </div>
                            <h2 className="text-2xl font-bold mb-2">¡Revisa tu correo!</h2>
                            <p className="text-muted-foreground mb-4">
                                Hemos enviado un enlace de verificación a<br />
                                <span className="font-medium text-foreground">{formData.email}</span>
                            </p>
                            <p className="text-sm text-red-500 font-medium mb-4">
                                ⚠️ Revisa también en Spam
                            </p>
                            <p className="text-sm text-muted-foreground mb-6">
                                Haz clic en el enlace para activar tu cuenta y comenzar a hacer predicciones.
                            </p>
                            <Button 
                                variant="outline" 
                                onClick={() => navigate('/login')}
                                className="w-full"
                            >
                                Ir a Iniciar Sesión
                            </Button>
                        </CardContent>
                    </Card>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-background to-primary/5 relative">
            {/* Theme toggle */}
            <button
                onClick={toggleTheme}
                className="absolute top-5 right-5 w-10 h-10 rounded-xl bg-card border border-border flex items-center justify-center transition-all hover:border-primary/50 hover:shadow-lg hover:shadow-primary/10 active:scale-90"
                data-testid="register-theme-toggle-main"
            >
                {theme === 'dark' ? <Sun className="w-5 h-5 text-amber-400" /> : <Moon className="w-5 h-5 text-slate-600" />}
            </button>
            <div className="w-full max-w-md">
                {/* Logo */}
                <div className="text-center mb-8">
                    <Link to="/" className="inline-flex items-center gap-2">
                        <Trophy className="w-10 h-10 text-primary" />
                        <span className="font-heading text-3xl font-black">POLLA REMAKIA</span>
                    </Link>
                </div>

                <Card className="border-border/50 shadow-xl">
                    <CardHeader className="text-center pb-2">
                        <CardTitle className="text-2xl font-bold">Crear Cuenta</CardTitle>
                        <CardDescription>
                            Regístrate para comenzar a hacer predicciones
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div className="space-y-2">
                                <label className="text-sm font-medium">Nombre completo</label>
                                <div className="relative">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        type="text"
                                        placeholder="Tu nombre"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        className="pl-10"
                                        required
                                        data-testid="register-name"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Correo electrónico</label>
                                <div className="relative">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        type="email"
                                        placeholder="tu@email.com"
                                        value={formData.email}
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        className="pl-10"
                                        required
                                        data-testid="register-email"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Contraseña</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Mínimo 6 caracteres"
                                        value={formData.password}
                                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                        className="pl-10 pr-10"
                                        required
                                        minLength={6}
                                        data-testid="register-password"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                                    >
                                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium">Confirmar contraseña</label>
                                <div className="relative">
                                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        type={showPassword ? "text" : "password"}
                                        placeholder="Repite tu contraseña"
                                        value={formData.confirmPassword}
                                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                                        className="pl-10"
                                        required
                                        data-testid="register-confirm-password"
                                    />
                                </div>
                            </div>

                            <Button
                                type="submit"
                                className="w-full"
                                disabled={loading}
                                data-testid="register-submit"
                            >
                                {loading ? (
                                    <span className="contents">
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Creando cuenta...
                                    </span>
                                ) : (
                                    'Crear Cuenta'
                                )}
                            </Button>

                            <div className="relative my-4">
                                <div className="absolute inset-0 flex items-center">
                                    <span className="w-full border-t border-border" />
                                </div>
                                <div className="relative flex justify-center text-xs uppercase">
                                    <span className="bg-card px-2 text-muted-foreground">o regístrate con</span>
                                </div>
                            </div>

                            <Button
                                type="button"
                                variant="outline"
                                className="w-full"
                                onClick={handleGoogleLogin}
                                disabled={googleLoading}
                                data-testid="google-register-btn"
                            >
                                {googleLoading ? (
                                    <span className="contents">
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        Conectando...
                                    </span>
                                ) : (
                                    <span className="contents">
                                        <GoogleIcon />
                                        Google
                                    </span>
                                )}
                            </Button>
                        </form>
                    </CardContent>
                    <CardFooter className="flex flex-col gap-4">
                        <div className="text-center text-sm text-muted-foreground">
                            ¿Ya tienes cuenta?{' '}
                            <Link 
                                to="/login" 
                                className="text-primary hover:underline font-medium"
                                data-testid="login-link"
                            >
                                Inicia sesión
                            </Link>
                        </div>
                    </CardFooter>
                </Card>
            </div>
        </div>
    );
}
